/**
 * prefers-reduced-motion helpers.
 *
 * Foundation primitive used by every anime.js hook in this directory.
 * Components should always honor the OS-level motion preference: if the
 * user prefers reduced motion, jump to the final frame instantly rather
 * than playing the animation.
 */

import { useEffect, useState } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Synchronous, SSR-safe read of the prefers-reduced-motion media query.
 * Returns `false` outside the browser so server renders never animate
 * (matches the safer "don't move things" default).
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(QUERY).matches;
}

/**
 * React hook variant that subscribes to the media query and re-renders
 * when the user toggles their OS motion preference live. Use inside
 * components that need to react to the change without a reload.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => prefersReducedMotion());

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(QUERY);
    const handler = (event: MediaQueryListEvent) => setReduced(event.matches);

    // Sync once in case the value changed between mount and effect
    setReduced(mql.matches);

    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }

    // Safari < 14 fallback
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  return reduced;
}
